/**
 * VendorCatalogShopService
 * Service for public shop endpoints in VendorCatalogManagement (read-only)
 * Route: api/shop
 * Controller: ShopController.cs (CCMS.Api.VendorCatalogManagement)
 */
import { BaseService } from '../base/BaseService';
/**
 * VendorCatalogShopService
 * Public read-only endpoints for shop product data.
 * All data is served from Redis cache on the backend.
 */
export class VendorCatalogShopService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorCatalogShopService.DEFAULT_SERVICE_PATH,
            basePath: VendorCatalogShopService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== PRODUCT ENDPOINTS ====================
    /**
     * Get all active products
     * GET /api/shop/products
     */
    async getProducts() {
        const response = await this.getProductsRaw();
        return response.data ?? [];
    }
    /**
     * Get all products (raw response)
     */
    getProductsRaw() {
        const endpoint = this.getEndpoint('products', 'products');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get a single product by ID with media and variants
     * GET /api/shop/products/{id}
     */
    async getProductById(id) {
        const response = await this.getProductByIdRaw(id);
        if (!response.data) {
            throw new Error(`Product ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get product by ID (raw response)
     */
    getProductByIdRaw(id) {
        const endpoint = this.getEndpoint('product-by-id', `products/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get active products for a specific vendor
     * GET /api/shop/products/vendor/{vendorId}
     */
    async getProductsByVendor(vendorId) {
        const response = await this.getProductsByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get products by vendor (raw response)
     */
    getProductsByVendorRaw(vendorId) {
        const endpoint = this.getEndpoint('products-by-vendor', `products/vendor/${vendorId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get active products filtered by type
     * GET /api/shop/products/by-type/{typeId}
     */
    async getProductsByType(typeId) {
        const response = await this.getProductsByTypeRaw(typeId);
        return response.data ?? [];
    }
    /**
     * Get products by type (raw response)
     */
    getProductsByTypeRaw(typeId) {
        const endpoint = this.getEndpoint('products-by-type', `products/by-type/${typeId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== EVENT CATALOG ENDPOINTS ====================
    /**
     * Get event catalog — event info, location info, and products grouped by vendor
     * GET /api/shop/events/{eventId}/catalog?page=1&pageSize=10
     */
    async getEventCatalog(eventId, page = 1, pageSize = 10) {
        const response = await this.getEventCatalogRaw(eventId, page, pageSize);
        if (!response.data) {
            throw new Error(`Event catalog for event ${eventId} not found`);
        }
        return response.data;
    }
    /**
     * Get event catalog (raw response)
     */
    getEventCatalogRaw(eventId, page = 1, pageSize = 10) {
        const endpoint = this.getEndpoint('event-catalog', `events/${eventId}/catalog`);
        const url = this.buildUrlWithParams(endpoint, { page, pageSize });
        return this.http.get(url);
    }
    // ==================== EVENT PRODUCTS ENDPOINT ====================
    /**
     * Get paginated products for an event from all confirmed vendors
     * GET /api/shop/events/{eventId}/products?skip=0&take=10
     */
    async getEventProducts(eventId, skip = 0, take = 10) {
        const response = await this.getEventProductsRaw(eventId, skip, take);
        return response.data ?? [];
    }
    /**
     * Get event products (raw response with pagination metadata)
     * Response includes totalCount, skip, take, hasMore on the envelope
     */
    getEventProductsRaw(eventId, skip = 0, take = 10) {
        const endpoint = this.getEndpoint('event-products', `events/${eventId}/products`);
        const url = this.buildUrlWithParams(endpoint, { skip, take });
        return this.http.get(url);
    }
    // ==================== FEATURED ENDPOINTS ====================
    /**
     * Get featured active products
     * GET /api/shop/products/featured
     */
    async getFeaturedProducts() {
        const response = await this.getFeaturedProductsRaw();
        return response.data ?? [];
    }
    /**
     * Get featured products (raw response)
     */
    getFeaturedProductsRaw() {
        const endpoint = this.getEndpoint('products-featured', 'products/featured');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
VendorCatalogShopService.DEFAULT_SERVICE_PATH = 'vendor-catalogs';
VendorCatalogShopService.BASE_PATH = 'api/shop';
//# sourceMappingURL=VendorCatalogShopService.js.map